import * as THREE from 'three';

const DISTANCE = 5.5;

/**
 * T3-4 (PLAN-CITY2 §2 — Karan's G2 playtest: "I kept feeling the need to
 * zoom out... I don't have an ability to zoom out at all"). Three fixed
 * presets rather than a free-drag zoom (matches this camera's own
 * "no smoothing on a deliberate player input" philosophy — `applyLook`'s
 * yaw/pitch drag is immediate, not eased, and a preset is the discrete
 * equivalent of that for distance). `normal` is exactly the pre-T3
 * `DISTANCE` constant, so a player who never touches this setting sees
 * ZERO behavior change. Interior `maxDistance` clamp logic (INTERIOR_
 * CAMERA_MAX_DISTANCE, world/interior.ts) is completely unchanged by this —
 * `OrbitCamera.update()`/`snapTo()` still take an explicit `maxDistance`
 * override for that case, which always wins over whatever outdoor zoom
 * preset is active (see `activeCameraMaxDistance()`, main.ts).
 */
export type CameraZoom = 'close' | 'normal' | 'far';
export const CAMERA_ZOOM_OPTIONS: CameraZoom[] = ['close', 'normal', 'far'];
export const CAMERA_ZOOM_LABELS: Record<CameraZoom, string> = { close: 'CLOSE', normal: 'NORMAL', far: 'FAR' };
export const CAMERA_ZOOM_DISTANCES: Record<CameraZoom, number> = { close: 3.8, normal: DISTANCE, far: 7.5 };
const MIN_DISTANCE = 1.2; // collision floor — never let the camera clip fully into the player
const TARGET_HEIGHT = 1.0; // orbit pivot at roughly chest height, not the capsule's feet
const MIN_PITCH = 0.15; // radians above the horizon plane — keeps the camera from going under the ground
const MAX_PITCH = 1.35; // radians — stops just short of straight-down, avoids gimbal-feeling flips
const DISTANCE_SMOOTHING = 0.25; // per-frame lerp toward the collision-resolved distance, not the raw angle

// T1 auto-follow tuning (Karan's first playtest: "the camera is not moving in
// the direction I go"). The follow is a RATE-CAPPED proportional chase, not an
// ease-to-target — see `followMovement`'s comment for why a target-seeking
// design cannot work here at all. Yaw turns at `FOLLOW_GAIN * theta` rad/s
// (theta = stick angle off camera-forward), hard-capped at MAX_FOLLOW_RATE.
// At the cap (~97°/s) a full quarter-turn comes around in ~0.9s; small
// corrections turn proportionally gentler. T2/T3 may retune both numbers.
const FOLLOW_GAIN = 2.0; // rad/s of yaw per radian of stick offset
export const MAX_FOLLOW_RATE = 1.7; // rad/s hard cap (~97°/s) — exported for the harness's rate-bound checks
// Inside this stick-vs-forward angle, don't hunt — the camera is already
// close enough to "behind movement," and a human thumb holding roughly-up
// jitters a few degrees every frame. Without the deadband that jitter keeps
// the camera micro-sawing forever.
const FOLLOW_DEADBAND = 0.12; // ~7°
// Below this move-input magnitude, treat the player as not meaningfully
// moving — do not chase a heading derived from stick noise/near-zero input.
const FOLLOW_DEADZONE = 0.05;
// If the movement heading points more than this far from where the camera
// currently looks, treat it as a backpedal (retreating toward the camera)
// rather than a turn — do not rotate, so retreating from an enemy never
// whips the camera 180° and hides the thing being retreated from. Compared
// directly against `theta` (the stick's angle relative to camera-forward,
// see `followMovement`'s own comment) — theta already IS that angle, no
// cosine/dot-product conversion needed.
const BACKPEDAL_ANGLE = (2 * Math.PI) / 3; // 120°
// How long a manual right-thumb drag suppresses auto-follow after the LAST
// nonzero drag delta — long enough that releasing the drag doesn't
// immediately get fought by the follow logic re-centering behind movement.
const DRAG_GRACE_SECONDS = 1.2;

/**
 * S01's real orbit camera: right-thumb drag sets yaw/pitch directly (no
 * smoothing on the angle itself — a dragged camera should feel immediate),
 * a fixed base distance is pulled in via `MIN_DISTANCE`..`DISTANCE` whenever
 * a raycast toward the desired position hits an occluder first (a building
 * wall, the tunnel, the rooftop parapet), and the resolved distance is
 * lightly smoothed so passing in/out of obstruction doesn't snap. Public
 * shape (camera, update(target), snapTo(target), setAspect) is S00's
 * `FollowCamera` seam; `applyLook`/`setOccluders`/`getForward`/`getRight`
 * are the new surface S01 adds for drag input and camera-relative movement.
 */
export class OrbitCamera {
  readonly camera: THREE.PerspectiveCamera;
  private yaw = 0;
  private pitch = 0.55;
  private currentDistance = DISTANCE;
  // T3-4: the player's chosen OUTDOOR pull-back distance (setZoom()) — the
  // default `maxDistance` for update()/snapTo() below when a caller doesn't
  // pass its own override. Starts at `DISTANCE` (== CAMERA_ZOOM_DISTANCES.
  // normal), so a boot before main.ts's own setZoom(save.settings.
  // cameraZoom) sync call behaves exactly like every pre-T3 build.
  private zoomDistance = DISTANCE;
  private occluders: THREE.Object3D[] = [];
  // Counts down from DRAG_GRACE_SECONDS on every nonzero applyLook delta;
  // auto-follow is suppressed while this is > 0. Decayed once per tick in
  // `update()` (the one call every tick already makes), not in
  // `followMovement()`, so the grace period still expires even on a tick
  // where the player isn't moving.
  private dragGraceTimer = 0;
  private readonly raycaster = new THREE.Raycaster();
  private readonly forward = new THREE.Vector3(0, 0, -1);
  private readonly right = new THREE.Vector3(1, 0, 0);
  // S08 memory audit: pivotAndDir() ran twice every tick (update() once,
  // occasionally snapTo() too) allocating 2 fresh Vector3 each time — a
  // small but CONSTANT cost (unlike the enemy/steering fixes above, this one
  // scales with frame rate, not entity count, so it never goes away even in
  // an empty scene). Both return values are consumed synchronously within
  // the same call chain (resolveDesiredDistance -> applyTransform) and never
  // stored, so reusing these is safe.
  private readonly scratchPivot = new THREE.Vector3();
  private readonly scratchDir = new THREE.Vector3();

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.05, 200);
  }

  setOccluders(objects: THREE.Object3D[]): void {
    this.occluders = objects;
  }

  /** Accumulate a right-thumb drag delta (screen pixels) into yaw/pitch. */
  applyLook(dxPixels: number, dyPixels: number, sensitivity: number): void {
    if (dxPixels !== 0 || dyPixels !== 0) {
      // Re-arms every dragging frame, not just once on drag-start — a
      // multi-second drag keeps pushing the grace window out so auto-follow
      // never kicks in mid-drag, then starts its countdown from the actual
      // moment the thumb lifts.
      this.dragGraceTimer = DRAG_GRACE_SECONDS;
    }
    this.yaw -= dxPixels * sensitivity;
    this.pitch = THREE.MathUtils.clamp(this.pitch - dyPixels * sensitivity, MIN_PITCH, MAX_PITCH);
  }

  /**
   * Soft auto-follow: turns the camera toward the direction the player is
   * pushing, so travel ends up on-screen without a manual drag every time
   * direction changes (Karan's first-playtest complaint). Takes the RAW
   * stick input (`moveX`/`moveY` — InputSystem's convention, y negative =
   * forward). Write `theta = atan2(-moveX, -moveY)` for the stick's angle
   * off camera-forward (the inverse of `pivotAndDir`'s
   * `forward = (-sin(yaw), 0, -cos(yaw))` formula).
   *
   * WHY THIS IS A BOUNDED-RATE CHASE AND NOT AN EASE-TO-TARGET — the
   * invariance property (T1 review, confirmed twice): movement is
   * camera-relative (`right*moveX + forward*-moveY`, controller.ts), and
   * the basis is derived from `this.yaw` — so rotating yaw rotates the
   * world movement direction WITH it, and the angle between camera-forward
   * and the movement direction is exactly `theta` at every yaw, at every
   * instant. No amount of yaw motion can shrink it while the stick is held
   * fixed. Two prior versions of this method failed on exactly that:
   * (1) easing toward `yaw + theta` recomputed every tick = a closed
   * feedback loop that spun ~300°/s forever on any held off-axis stick;
   * (2) LATCHING `yaw + theta` once fixed the constant-keyboard case but a
   * real touch thumb jitters several degrees per tick, and every re-latch
   * past the jitter threshold re-injected a fresh full-theta gap — the same
   * spin at a coarser cadence.
   *
   * The correct genre-standard design accepts the invariance instead of
   * fighting it: turn yaw toward the stick's side at a rate PROPORTIONAL to
   * theta and HARD-CAPPED at MAX_FOLLOW_RATE, with a deadband where it
   * stops. Consequences, all intended:
   *  - A human steering toward something on screen re-centers their thumb
   *    as the view comes around; theta shrinks under the deadband and the
   *    camera settles behind travel. (The harness models this as a stick
   *    that tracks a fixed WORLD heading — that closed loop converges.)
   *  - A robotically-held off-axis stick (held D key) arcs the player in a
   *    wide circle at a bounded, comfortable rate — the same thing Zelda/
   *    Genshin-class follow cameras do — instead of spinning uncontrollably.
   *    Rate is bounded by construction: jitter can only modulate the rate,
   *    never inject position gaps, so there is no runaway mode at all.
   */
  followMovement(moveX: number, moveY: number, dt: number): void {
    if (this.dragGraceTimer > 0) return; // manual drag still owns the framing
    const mag = Math.hypot(moveX, moveY);
    if (mag < FOLLOW_DEADZONE) return; // not meaningfully moving
    const theta = Math.atan2(-moveX, -moveY); // stick angle vs. camera-forward
    // A NaN component would slip through every guard below (NaN compares
    // false to everything) and poison yaw permanently — unreachable from
    // today's stick/keyboard input, but this method is on the public
    // __BR3D__ surface and a future gamepad path could feed it anything.
    if (!Number.isFinite(theta)) return;
    // Backpedal: a movement heading pointing mostly the OPPOSITE way (toward
    // the camera, i.e. retreating on-screen) must not spin the view around.
    if (Math.abs(theta) > BACKPEDAL_ANGLE) return;
    if (Math.abs(theta) < FOLLOW_DEADBAND) return; // already close enough — don't hunt thumb jitter
    const rate = THREE.MathUtils.clamp(FOLLOW_GAIN * theta, -MAX_FOLLOW_RATE, MAX_FOLLOW_RATE);
    this.yaw += rate * dt;
  }

  /** Pivot + direction the camera looks from, for the CURRENT yaw/pitch. */
  private pivotAndDir(target: THREE.Vector3): { pivot: THREE.Vector3; dir: THREE.Vector3 } {
    const pivot = this.scratchPivot.set(target.x, target.y + TARGET_HEIGHT, target.z);
    const sinPitch = Math.sin(this.pitch);
    const cosPitch = Math.cos(this.pitch);
    const dir = this.scratchDir.set(Math.sin(this.yaw) * cosPitch, sinPitch, Math.cos(this.yaw) * cosPitch);
    return { pivot, dir };
  }

  /**
   * The occlusion-resolved distance for the given pivot/dir — no smoothing
   * applied here. `maxDistance` defaults to the outdoor `DISTANCE` constant
   * — every pre-S05 call site keeps its exact prior behavior unchanged.
   *
   * S05 review finding (HIGH, dont-break-list lens, live-reproduced): the
   * bodega interior (world/interior.ts) is a ~7×6m room with a 2.6m
   * ceiling, far smaller than the outdoor scale this camera was tuned for.
   * At the default pitch (0.55rad) the ray's height climbs ~0.61m per 1m
   * of horizontal travel — over the room's own real wall distances (3-4.5m
   * from the spawn point) the ray has already climbed above the 2.6m walls
   * before it would ever reach one, so the occlusion raycast never finds a
   * hit and always resolves the full un-occluded outdoor `DISTANCE` (5.5m),
   * landing the camera outside/above the room in every direction, every
   * time — confirmed live against the real code, not a hypothetical. A
   * smaller room can't fix this on its own without becoming uncomfortably
   * cramped (the geometry only works if literally every wall stays within
   * ~2.6m of wherever the player is standing); the correct fix is what
   * every game with both outdoor and indoor scales already does — a
   * smaller default pull-back distance for tight interior spaces. main.ts
   * passes a smaller `maxDistance` for both `update()` and `snapTo()` while
   * the player is inside the bodega (tracked via `insideBodega`), reverting
   * to the default the moment they exit.
   */
  private resolveDesiredDistance(pivot: THREE.Vector3, dir: THREE.Vector3, maxDistance: number): number {
    if (this.occluders.length === 0) return maxDistance;
    this.raycaster.set(pivot, dir);
    this.raycaster.far = maxDistance;
    // S09: recursive=true (was false) — `occluders` is `block.group.children`,
    // and every occluder used to be a leaf Mesh (a gray box), so non-
    // recursive and recursive were identical in practice. Once real C04 art
    // landed, most of `block.group`'s children became GROUPS (a loaded GLB's
    // whole scene, wrapping its real mesh geometry as descendants) — a bare
    // Group has no geometry of its own, so a non-recursive raycast against
    // it can NEVER register a hit, silently disabling occlusion against
    // every building/prop the moment its gray-box placeholder was replaced
    // (confirmed live: the harness's own wall-occlusion check regressed
    // from < 5.0m to 6.06m the instant building_a's facade loaded). The
    // InstancedMesh ground tiles are unaffected either way (InstancedMesh
    // IS a Mesh subclass, always raycastable directly).
    const hits = this.raycaster.intersectObjects(this.occluders, true);
    if (hits.length === 0) return maxDistance;
    return THREE.MathUtils.clamp(hits[0].distance - 0.3, MIN_DISTANCE, maxDistance);
  }

  /**
   * `dt` is used ONLY to decay the manual-drag grace timer — it has no
   * effect on distance smoothing (that stays a fixed per-frame lerp factor,
   * unchanged from S01) or on yaw/pitch (still driven solely by
   * `applyLook`/`followMovement`, not by this method). Decaying here, not
   * in `followMovement`, means the grace period still counts down on ticks
   * where the player isn't moving (followMovement wouldn't run that tick).
   * `dt` defaults to 0 so a legacy one-arg `update(target)` call (an old
   * probe/harness script) just freezes the grace timer that tick instead of
   * poisoning it with NaN (`timer - undefined` is NaN, and a NaN timer
   * would disable auto-follow permanently since `NaN > 0` is false forever).
   * `maxDistance` defaults to `this.zoomDistance` (T3-4's zoom-preset field
   * — see `resolveDesiredDistance`'s own comment) — every existing 1-2 arg
   * call site is unaffected, and main.ts's outdoor `activeCameraMaxDistance()`
   * passing `undefined` still resolves to whatever zoom preset is active
   * (JS default params trigger on `undefined`, not just omission).
   */
  update(target: THREE.Vector3, dt = 0, maxDistance = this.zoomDistance): void {
    if (this.dragGraceTimer > 0) this.dragGraceTimer = Math.max(0, this.dragGraceTimer - dt);
    const { pivot, dir } = this.pivotAndDir(target);
    const desiredDistance = this.resolveDesiredDistance(pivot, dir, maxDistance);
    this.currentDistance = THREE.MathUtils.lerp(this.currentDistance, desiredDistance, DISTANCE_SMOOTHING);
    this.applyTransform(pivot, dir);
  }

  /** Positions/aims the camera at `this.currentDistance` and refreshes the movement basis. Callers set currentDistance first. */
  private applyTransform(pivot: THREE.Vector3, dir: THREE.Vector3): void {
    this.camera.position.copy(pivot).addScaledVector(dir, this.currentDistance);
    this.camera.lookAt(pivot);

    // Cache flattened, normalized basis vectors for the controller's
    // camera-relative movement — recomputed here so they're always in sync
    // with the yaw actually rendered this frame.
    this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw)).normalize();
    // right = cross(forward, worldUp) = (-forward.z, 0, forward.x). An
    // earlier version had this backward ((forward.z, 0, -forward.x)) — it
    // never affected pure forward/back movement (dirX cancels out when
    // move.x is 0) but silently inverted every LEFT/RIGHT strafe, caught by
    // a dedicated strafe-past-a-landmark harness check, not by the
    // forward-only movement check that was already passing.
    this.right.set(-this.forward.z, 0, this.forward.x);
  }

  /** Flattened (Y=0), normalized camera-forward — for camera-relative move input. */
  getForward(): THREE.Vector3 {
    return this.forward;
  }

  /** Flattened (Y=0), normalized camera-right — for camera-relative move input. */
  getRight(): THREE.Vector3 {
    return this.right;
  }

  /** Current yaw (radians) — for callers composing a RELATIVE facing for snapTo's `yaw` param (the bodega exit's +π flip, main.ts). */
  getYaw(): number {
    return this.yaw;
  }

  /**
   * Cut straight to framing the target with no lerp — call once at startup
   * (frame 1 runs zero update() ticks) and after any teleport, matching
   * S00's `FollowCamera.snapTo` discipline exactly. Resolves occlusion
   * directly into `currentDistance` rather than routing through `update()`
   * (which always re-smooths toward the resolved distance) — a teleport
   * into an occluded spot (e.g. the dev-tap teleport landing the player
   * against a wall) previously only closed ~25% of the gap on the "snap"
   * frame and visibly slid in over the next ~10 frames, exactly the
   * "camera slides across the map" symptom snapTo exists to prevent
   * (confirmed by a 3-lens review). `maxDistance` defaults to
   * `this.zoomDistance` (T3-4) — see `resolveDesiredDistance`'s own comment
   * (S05's interior-camera fix).
   *
   * `yaw` (optional, T3-1 follow-up): an explicit facing for the cut.
   * snapTo never used to touch yaw, which is right for same-scene teleports
   * (respawn, dev-tap) — but a DOOR transition between two scenes whose
   * "outward" directions are opposite world headings (the bodega interior's
   * doorway is +z, the street side of the exterior door is -z) preserves a
   * now-backward yaw, so a held stick marches the player straight back at
   * the door they just came through (review-confirmed: movement is
   * camera-relative, so held input's world direction is invariant across a
   * yaw-preserving cut). Callers that ARE door transitions pass the
   * destination's natural facing; everyone else omits it and keeps today's
   * behavior exactly.
   */
  snapTo(target: THREE.Vector3, maxDistance = this.zoomDistance, yaw?: number): void {
    if (yaw !== undefined) this.yaw = yaw;
    const { pivot, dir } = this.pivotAndDir(target);
    this.currentDistance = this.resolveDesiredDistance(pivot, dir, maxDistance);
    this.applyTransform(pivot, dir);
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * T3-4: sets the OUTDOOR pull-back preset. Deliberately does NOT re-snap
   * or force an immediate `update()`/`snapTo()` call here — the next real
   * per-frame `update()` (unconditional, every playing tick) picks up the
   * new `zoomDistance` as its default `maxDistance` and eases toward it via
   * the SAME `DISTANCE_SMOOTHING` lerp an occlusion change already uses, so
   * a live zoom change reads as a smooth push/pull, not a jump cut — the
   * one exception is main.ts's own boot-time sync call, which runs before
   * the very first `snapTo()` and therefore IS effectively instant (frame 1
   * has run zero `update()` ticks, matching every other "sync before the
   * first snapTo()" boot-order note in this file/main.ts).
   */
  setZoom(zoom: CameraZoom): void {
    this.zoomDistance = CAMERA_ZOOM_DISTANCES[zoom];
  }
}
